import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import octopusInstance from "../../services/octopus/octopusInstance";
import { fetchOptionChainAction } from "../../store/actions/watchlist";
import { getLtp } from "../../services/backendAPI";
import OptionsHeader from "./OptionsHeader";
import Box from "@mui/material/Box";
import CallPutInstruments from "./CallPutInstruments";
import Paper from "@mui/material/Paper";
import { getExchangeCode } from "../../services/exchanges";
import InsertChartOutlinedTwoToneIcon from '@mui/icons-material/InsertChartOutlined';
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";


// ── SessionStorage helpers ──────────────────────────────────────────
const SS_KEYS = {
  NUM: 'oc_strikeLength',
  COLUMNS: 'oc_customColumns',
};

const readSS = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return fallback;
    const parsed = typeof fallback === 'object' ? JSON.parse(raw) : Number(raw);
    // If we're reading columns and it's the old nested object format, use fallback
    if (key === SS_KEYS.COLUMNS && parsed && !Array.isArray(parsed)) {
      return fallback;
    }
    return parsed;
  } catch { return fallback; }
};

const writeSS = (key, value) => {
  try {
    sessionStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
  } catch { /* quota exceeded – silent */ }
};

// ── Expiry Date Parsing Helper ────────────────────────────────────────
const monthMap = {
  'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
  'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
};

const parseExpiryDate = (dateStr) => {
  try {
    const match = dateStr.match(/(\d{2})([A-Z]{3})(\d{2})/);
    if (!match) return null;
    const [_, day, month, year] = match;
    const fullYear = 2000 + parseInt(year);
    const monthIndex = monthMap[month];
    return new Date(fullYear, monthIndex, parseInt(day));
  } catch {
    return null;
  }
};

// ── ATM slice helper (matches captur's handleStrikeLengthChange) ────
const findClosestStrikeIndex = (strikes, ltp) => {
  if (!strikes || strikes.length === 0) return -1;
  if (ltp == null) return Math.floor(strikes.length / 2);
  let closestIndex = 0;
  let minDiff = Math.abs(strikes[0].strike_price - ltp);
  for (let i = 1; i < strikes.length; i++) {
    const diff = Math.abs(strikes[i].strike_price - ltp);
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = i;
    }
  }
  return closestIndex;
};

// ── Exact Symmetric Slice around ATM Line ───────────────────────────
const sliceStrikesAroundATM = (strikes, ltp, num) => {
  if (!strikes || strikes.length === 0) return [];
  if (num >= strikes.length) return strikes;

  // Find the exact index where the ATM line will be drawn.
  // The ATM line is drawn between `curr` and `next` where `curr <= ltp < next`.
  let centerIndex = -1;
  let left = 0, right = strikes.length - 1;
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const curr = parseFloat(strikes[mid]?.strike_price || 0);
    const next = parseFloat(strikes[mid + 1]?.strike_price || Infinity);
    if (curr <= ltp && ltp < next) {
      centerIndex = mid;
      break;
    }
    if (ltp < curr) right = mid - 1;
    else left = mid + 1;
  }

  // Fallback if not found (e.g. LTP outside range)
  if (centerIndex === -1) {
    centerIndex = findClosestStrikeIndex(strikes, ltp);
  }

  // We want EXACTLY half above and half below the ATM line.
  // E.g., for num=6, we want 3 before the line (including centerIndex)
  // and 3 after the line.
  const half = Math.floor(num / 2);

  let startIndex = centerIndex - half + 1;
  let endIndex = centerIndex + half + 1;

  // Clamp to bounds while preserving exactly `num` rows if possible
  if (startIndex < 0) {
    endIndex = Math.min(strikes.length, endIndex + Math.abs(startIndex));
    startIndex = 0;
  } else if (endIndex > strikes.length) {
    startIndex = Math.max(0, startIndex - (endIndex - strikes.length));
    endIndex = strikes.length;
  }

  return strikes.slice(startIndex, endIndex);
};

const DEFAULT_COLUMNS = [
  "ltp", "change", "change%", "close", "volume"
];

const StyledCheck = styled('div')(({ theme }) => ({
  [theme.breakpoints.up("sm")]: {
    right: '2%',
    marginTop: 0,
  },
  '@media (max-width: 765px)': {
    marginTop: 0,
  },
}));

function OptionChainOverview({ exchange, token, scripInfo }) {

  const dispatch = useDispatch();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [selectedExpiryDate, setSelectedExpiryDate] = useState(null);

  // Calculate the correct underlying token once, avoiding duplication
  const resolvedToken = useMemo(() => {
    return exchange === "MCX" && scripInfo?.instrument_name?.startsWith("FUT") ?
      scripInfo?.instrument_token :
      exchange === "BSE" && scripInfo?.is_index ?
        scripInfo?.instrument_token : 
        (scripInfo?.underlying_token || scripInfo?.instrument_token);
  }, [exchange, scripInfo]);

  // ── All expiry data from API (fetched ONCE per instrument) ──
  const [allExpiryData, setAllExpiryData] = useState([]);

  // ── Visible option chain (sliced from allExpiryData) ──
  const [optionChain, setOptionChain] = useState([]);

  const [chartQuery, setChartQuery] = useState({});
  const [switchViews, setSwitchViews] = useState(false);

  // ── Persisted in sessionStorage ──
  const [num, setNum] = useState(() => readSS(SS_KEYS.NUM, 18));
  const [selectedColumns, setSelectedColumns] = useState(() => readSS(SS_KEYS.COLUMNS, DEFAULT_COLUMNS));

  // ── Underlying LTP stored as clean number (not raw WS msg) ──
  const [underlyingLtp, setUnderlyingLtp] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Track whether the initial API fetch has been done for this instrument
  const fetchedKeyRef = useRef(null);

  // ── Persist num to sessionStorage ──
  const handleNumChange = useCallback((newNum) => {
    setNum(newNum);
    writeSS(SS_KEYS.NUM, newNum);
  }, []);

  // ── Persist columns to sessionStorage ──
  const handleColumnsChange = useCallback((newCols) => {
    setSelectedColumns(prev => {
      const resolvedCols = typeof newCols === 'function' ? newCols(prev) : newCols;
      writeSS(SS_KEYS.COLUMNS, resolvedCols);
      return resolvedCols;
    });
  }, []);

  // Keep a ref of the latest LTP for slicing (avoids re-triggering effect on every tick)
  const ltpRef = useRef(null);
  useEffect(() => {
    if (underlyingLtp != null) {
      ltpRef.current = underlyingLtp;
    }
  }, [underlyingLtp]);

  // ── When allExpiryData or selectedExpiryDate or num changes, slice client-side ──
  useEffect(() => {
    if (!allExpiryData || allExpiryData.length === 0) return;

    // Find the expiry data for the selected date
    const expiryObj = allExpiryData.find(e => e.expiry_date === selectedExpiryDate);
    if (!expiryObj || !expiryObj.strikes?.length) {
      setOptionChain([]);
      return;
    }

    // Client-side slice around ATM — NO API call
    // Uses ltpRef.current so this doesn't re-run on every underlying tick
    const slicedStrikes = sliceStrikesAroundATM(expiryObj.strikes, ltpRef.current, num);

    // Wrap in the same format the rest of the component expects
    setOptionChain([{
      expiry_date: expiryObj.expiry_date,
      strikes: slicedStrikes
    }]);
  }, [allExpiryData, selectedExpiryDate, num]);

  // ── Reset state when instrument changes ──
  useEffect(() => {
    setUnderlyingLtp(null);
    setHasFetched(false);
    setAllExpiryData([]);
    setOptionChain([]);
    setSelectedExpiryDate(null);
    setLoading(true); // Always start in loading state to prevent Empty State flicker
    fetchedKeyRef.current = null;
    ltpRef.current = null;
  }, [exchange, token]);

  // ── Fetch underlying LTP + subscribe to WS ──
  useEffect(() => {
    if (resolvedToken) {
      const cashExchange = (exchange === "NFO" || exchange === "NSE") ? "NSE" : 
                           (exchange === "BFO" || exchange === "BSE") ? "BSE" : exchange;
      
      // Fetch initial LTP via HTTP API first to avoid loading freeze
      getLtp({ exchange: cashExchange, token: resolvedToken })
        .then((response) => {
          if (response && response.data) {
            const ltpPrice = typeof response.data === 'object' 
              ? response.data.last_trade_price || response.data.ltp 
              : response.data;
            if (ltpPrice && ltpPrice !== 0) {
              setUnderlyingLtp(ltpPrice);
              setHasFetched(true);
            } else {
              setLoading(false); // If no valid LTP, stop loading
            }
          } else {
            setLoading(false); // If no data, stop loading
          }
        })
        .catch(err => {
          console.error("Error fetching initial LTP in OptionChainOverview:", err);
          setLoading(false); // On error, stop loading so it shows empty state
        });

      // Also subscribe to WebSocket for real-time updates
      const subscriptionLocation = `optchain-undLying-NSE-${resolvedToken}`;
      const handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation,
        payload: {
          exchangeCode: getExchangeCode(cashExchange),
          instrumentToken: resolvedToken,
        },
      });

      handler.subscribe(({ msg }) => {
        const newLtp = msg.ltp || msg.lastTradedPrice;
        if (newLtp && newLtp !== 0) {
          setUnderlyingLtp(newLtp);
          setHasFetched(true);
        }
      });

      return handler.unsubscribe;
    }
  }, [scripInfo, exchange, resolvedToken]);

  // ── Fetch option chain ONCE when LTP is available ──
  useEffect(() => {
    if (!hasFetched) return;

    // Unique key for this instrument — prevent duplicate fetches
    const fetchKey = `${exchange}-${resolvedToken}`;
    if (fetchedKeyRef.current === fetchKey) return;
    fetchedKeyRef.current = fetchKey;

    setLoading(true);

    const requestPayload = {
      exchange: (exchange === "NSE" || exchange === "NFO") ? "NFO" :
                (exchange === "BSE" || exchange === "BFO") ? "BFO" : exchange,
      token: resolvedToken,
      num: 30,  // Fetch 30 strikes (like captur), not the UI `num` value
      price: underlyingLtp,
      instrument_name: scripInfo?.instrument_name
    };
    
    dispatch(fetchOptionChainAction(requestPayload))
      .then((result) => {
        if (result && result.length > 0) {
          const now = new Date();
          now.setHours(0, 0, 0, 0); // Include dates expiring today

          const filteredAndSorted = result.filter(item => {
            const d = parseExpiryDate(item.expiry_date);
            return d && d >= now;
          }).sort((a, b) => {
            return parseExpiryDate(a.expiry_date) - parseExpiryDate(b.expiry_date);
          });

          // Use filtered data if available, otherwise fallback to raw result
          const finalData = filteredAndSorted.length > 0 ? filteredAndSorted : result;
          
          setAllExpiryData(finalData);

          // Default to the first available expiry
          let defaultExpiry = finalData[0].expiry_date;

          if (!selectedExpiryDate) {
            setSelectedExpiryDate(defaultExpiry);
          }
        }
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
      });
  }, [hasFetched]);

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{
        width: "100%",
        height: "100%",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            flex: '0 0 auto'
          }}
        >
          <Box
            sx={{
              px: 0,
              pt: { xs: 0, sm: 1.5 },
              pb: { xs: 0, sm: 0.5 },
              display: "flex",
              justifyContent: "flex-end",
              bgcolor: "background.paper"
            }}
            component={StyledCheck}
          >
            {allExpiryData.length > 0 ? (
              <OptionsHeader
                display_name={scripInfo.display_name}
                trading_symbol={scripInfo.trading_symbol}
                exchange={exchange}
                token={scripInfo?.instrument_token}
                num={num}
                setNum={handleNumChange}
                isIndex={scripInfo?.is_index}
                setSwitchViews={setSwitchViews}
                setChartQuery={setChartQuery}
                instrument={scripInfo}
                expiryDates={allExpiryData.map(item => item.expiry_date)}
                selectedExpiryDate={selectedExpiryDate}
                setSelectedExpiryDate={setSelectedExpiryDate}
                selectedColumns={selectedColumns}
                setSelectedColumns={handleColumnsChange}
              />
            ) : null}
          </Box>
        </Box>

        <Box sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          px: 0,
          pt: 0,
          pb: 0,
          touchAction: 'auto',
        }}>
          {loading ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
              textAlign: 'center'
            }}>
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main
                }}
              />
              <Typography
                variant="h6"
                color="textSecondary"
                sx={{
                  fontWeight: 500,
                  letterSpacing: '0.5px'
                }}
              >
                Loading Option Chain...
              </Typography>
            </Box>
          ) : optionChain.length > 0 ? (
            <Box sx={{
              width: '100%',
              flex: 1,
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              touchAction: 'auto',
            }}>
              {optionChain
                .filter(row => row.expiry_date === selectedExpiryDate)
                .map((row, index) => (
                  <CallPutInstruments
                    key={index}
                    row={row}
                    instrument={scripInfo}
                    panel={`panel${index}`}
                    setChartQuery={setChartQuery}
                    setSwitchViews={setSwitchViews}
                    exchange={exchange}
                    underlyingToken={resolvedToken}
                    underlineTradingSymbol={scripInfo?.trading_symbol}
                    underlyingLtp={underlyingLtp}
                    selectedColumns={selectedColumns}
                  />
                ))}
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
              textAlign: 'center'
            }}>
              <InsertChartOutlinedTwoToneIcon
                color="disabled"
                sx={{
                  fontSize: "4rem",
                  opacity: 0.5
                }}
              />
              {(() => {
                return (
                  <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 500, letterSpacing: '0.5px' }}>
                    Option Chain Not Available
                  </Typography>
                );
              })()}
            </Box>
          )}
        </Box>
      </Box>
    </Paper >
  );
}

export default OptionChainOverview; 